import client from './client';
import { fetchAllPages } from './fetchAllPages';
import { applyApprovedOnlyParams, applyScopedParams, clampPayloadToCurrentScope, filterItemsByCurrentScope } from './viewerAccess';
import { publishCollectionChange } from '../utils/collectionSync';
import { extractCollectionResponse } from './collectionResponse';
import { withOfflineCollectionSupport } from './offlineCollectionSupport';

function trimOrNull(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
}

function cleanQueryValue(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;
  const text = String(value).trim();
  return text ? text : null;
}

function buildQueryParams(params = {}, defaults = {}) {
  const query = { ...defaults };

  Object.entries(applyScopedParams(applyApprovedOnlyParams(params))).forEach(([key, value]) => {
    const cleaned = cleanQueryValue(value);
    if (cleaned === null || cleaned === undefined) return;
    query[key] = cleaned;
  });

  return query;
}

function unwrapResponse(responseData) {
  return responseData?.data ?? responseData ?? {};
}

function extractCollection(responseData, normalize) {
  return extractCollectionResponse(responseData, normalize);
}

function extractItem(responseData, normalize, targetUuid) {
  const payload = unwrapResponse(responseData);
  const candidates = [payload, payload?.data];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (Array.isArray(candidate.items)) {
      const match = targetUuid
        ? candidate.items.find((item) => String(item?.uuid ?? item?.id ?? '') === String(targetUuid))
        : candidate.items[0];

      if (match) return normalize(match);
    }

    if (candidate.uuid || candidate.id) {
      return normalize(candidate);
    }
  }

  return normalize({});
}

function makeCollectionApi({ basePath, normalize, buildPayload, collectionKey, defaultPerPage = 25 }) {
  const syncKey = collectionKey ?? basePath.replace(/^\/v1\//, '').replace(/^\//, '').replace(/\/$/, '');

  async function fetchPage(params = {}) {
    const { data } = await client.get(basePath, {
      params: buildQueryParams(params, {
        per_page: params.per_page ?? defaultPerPage,
        page: params.page ?? 1,
      }),
    });
    const page = extractCollection(data, normalize);
    return {
      ...page,
      items: filterItemsByCurrentScope(page.items),
    };
  }

  return {
    collectionKey: syncKey,

    async list(params = {}) {
      return fetchPage(params);
    },

    async listAll(params = {}) {
      return fetchAllPages((page) => fetchPage({ ...params, page }));
    },

    async get(uuid) {
      const { data } = await client.get(`${basePath}/${uuid}`);
      return extractItem(data, normalize, uuid);
    },

    async create(payload) {
      const { data } = await client.post(basePath, buildPayload(payload));
      const item = extractItem(data, normalize);
      publishCollectionChange(syncKey, { action: 'create', uuid: item?.uuid ?? null });
      return item;
    },

    async update(uuid, payload) {
      const { data } = await client.put(`${basePath}/${uuid}`, buildPayload(payload));
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'update', uuid });
      return item;
    },

    async remove(uuid) {
      const { data } = await client.delete(`${basePath}/${uuid}`);
      publishCollectionChange(syncKey, { action: 'remove', uuid });
      return data;
    },

    async submit(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/submit`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'submit', uuid });
      return item;
    },

    async validate(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/validate`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'validate', uuid });
      return item;
    },

    async approve(uuid) {
      const { data } = await client.post(`${basePath}/${uuid}/approve`);
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'approve', uuid });
      return item;
    },

    async reject(uuid, notes = '') {
      const { data } = await client.post(`${basePath}/${uuid}/reject`, { notes: trimOrNull(notes) });
      const item = extractItem(data, normalize, uuid);
      publishCollectionChange(syncKey, { action: 'reject', uuid });
      return item;
    },
  };
}

function humanize(value) {
  return String(value ?? '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toBoolean(value) {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    if (['true', 'yes', 'y', 'on', 'yes_first'].includes(text)) return true;
    if (['false', 'no', 'n', 'off', 'no_repeat'].includes(text)) return false;
  }
  return Boolean(value);
}

// DCIC-13 first_occurrence is a controlled ENUM (yes_first / no_repeat), not
// a boolean — accepts legacy boolean/short-string values already stored
// from before this was corrected, and always normalizes back to the enum.
function toFirstOccurrenceEnum(value) {
  if (value === true || value === 'yes' || value === 'yes_first') return 'yes_first';
  if (value === false || value === 'no' || value === 'no_repeat') return 'no_repeat';
  return 'yes_first';
}

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function toArray(value) {
  if (Array.isArray(value)) return value.filter((item) => item !== null && item !== undefined && String(item).trim() !== '');
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [value];
}

function toArrayOrNull(value) {
  const list = toArray(value);
  return list.length ? list : [];
}

function normalizeConflictRecord(row = {}) {
  const incidentDate = row.incident_date ?? row.incidentDate ?? '';
  const conflictType = row.conflict_type ?? row.conflictType ?? '';
  const severityLevel = row.severity_level ?? row.severityLevel ?? '';
  const resolutionStatus = row.resolution_status ?? row.resolutionStatus ?? '';
  const firstOccurrence = row.first_occurrence ?? row.firstOccurrence ?? false;
  const dataStatus = row.data_status ?? row.dataStatus ?? 'draft';
  const wardCommunity = row.ward_community ?? row.wardCommunity ?? '';
  const sexOfPrimaryParties = row.sex_of_primary_parties ?? row.sexOfPrimaryParties ?? '';
  const ageGroupOfParties = row.age_group_of_parties ?? row.ageGroupOfParties ?? '';
  const partiesProductionType = row.parties_production_type ?? row.partiesProductionType ?? '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    state: row.state ?? '',
    lga: row.lga ?? '',
    ward_community: wardCommunity,
    wardCommunity,
    ward: wardCommunity,
    community: wardCommunity,
    incident_date: incidentDate,
    incidentDate,
    conflict_type: conflictType,
    conflictType: humanize(conflictType),
    severity_level: severityLevel,
    severityLevel: humanize(severityLevel),
    resolution_status: resolutionStatus,
    resolutionStatus: humanize(resolutionStatus),
    sex_of_primary_parties: sexOfPrimaryParties,
    sexOfPrimaryParties: sexOfPrimaryParties,
    age_group_of_parties: ageGroupOfParties,
    ageGroupOfParties: ageGroupOfParties,
    parties_production_type: Array.isArray(partiesProductionType) ? partiesProductionType : toArrayOrNull(partiesProductionType),
    partiesProductionType: Array.isArray(partiesProductionType) ? partiesProductionType : toArrayOrNull(partiesProductionType),
    parties_involved: Array.isArray(row.parties_involved) ? row.parties_involved : [],
    partiesInvolved: Array.isArray(row.parties_involved) ? row.parties_involved : [],
    cause: row.cause ?? '',
    causeOfConflict: row.cause ?? '',
    livestock_lost: row.livestock_lost ?? null,
    livestockLost: row.livestock_lost ?? null,
    human_casualties: row.human_casualties ?? row.humanCasualties ?? null,
    humanCasualties: row.human_casualties ?? row.humanCasualties ?? null,
    estimated_economic_loss_ngn: row.estimated_economic_loss_ngn ?? null,
    estimatedEconomicLossNgn: row.estimated_economic_loss_ngn ?? null,
    estimatedEconomicLoss: row.estimated_economic_loss_ngn != null ? `NGN ${row.estimated_economic_loss_ngn}` : '',
    livelihood_impact: row.livelihood_impact ?? row.livelihoodImpact ?? '',
    livelihoodImpact: row.livelihood_impact ?? row.livelihoodImpact ?? '',
    evidence_attachment: row.evidence_attachment ?? '',
    evidenceAttachment: row.evidence_attachment ?? '',
    evidence_attachment_file_name: row.evidence_attachment_file_name ?? row.evidenceAttachmentFileName ?? '',
    evidenceAttachmentFileName: row.evidence_attachment_file_name ?? row.evidenceAttachmentFileName ?? '',
    asset_damage: row.asset_damage ?? row.assetDamage ?? '',
    assetDamage: row.asset_damage ?? row.assetDamage ?? '',
    first_occurrence: toFirstOccurrenceEnum(firstOccurrence),
    firstOccurrence: toFirstOccurrenceEnum(firstOccurrence),
    reporting_authority: row.reporting_authority ?? row.reportingAuthority ?? '',
    reportingAuthority: row.reporting_authority ?? row.reportingAuthority ?? '',
    linked_committee_id: row.linked_committee_id ?? row.linkedCommitteeId ?? '',
    linkedCommitteeId: row.linked_committee_id ?? row.linkedCommitteeId ?? '',
    data_flow_validation_level: row.data_flow_validation_level ?? '',
    dataFlowValidationLevel: row.data_flow_validation_level ?? '',
    data_status: dataStatus,
    dataStatus,
    validationStatus: humanize(dataStatus),
    submitted_by: row.submitted_by ?? null,
    submitted_at: row.submitted_at ?? null,
    validated_by: row.validated_by ?? null,
    validated_at: row.validated_at ?? null,
    approved_by: row.approved_by ?? null,
    approved_at: row.approved_at ?? null,
    rejected_at: row.rejected_at ?? null,
    rejection_notes: row.rejection_notes ?? null,
    created_by: row.created_by ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    raw: row,
  };
}

function normalizeCommitteeRecord(row = {}) {
  const chairperson = row.chairperson && typeof row.chairperson === 'object' ? row.chairperson : null;
  const dataStatus = row.data_status ?? row.dataStatus ?? 'draft';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    state: row.state ?? '',
    lga: row.lga ?? '',
    ward_community: row.ward_community ?? row.wardCommunity ?? '',
    wardCommunity: row.ward_community ?? row.wardCommunity ?? '',
    committee_name: row.committee_name ?? '',
    committeeName: row.committee_name ?? '',
    committee_type: row.committee_type ?? '',
    committeeType: humanize(row.committee_type),
    establishment_authority: row.establishment_authority ?? '',
    establishmentAuth: row.establishment_authority ?? '',
    established_date: row.established_date ?? '',
    establishedDate: row.established_date ?? '',
    chairperson_actor_uuid: chairperson?.uuid ?? row.chairperson_actor_uuid ?? null,
    chairpersonActorUuid: chairperson?.uuid ?? row.chairperson_actor_uuid ?? null,
    chairperson: chairperson?.full_name ?? chairperson?.name ?? row.chairperson ?? '',
    meeting_frequency: row.meeting_frequency ?? '',
    meetingFrequency: humanize(row.meeting_frequency),
    last_meeting_date: row.last_meeting_date ?? '',
    lastMeetingDate: row.last_meeting_date ?? '',
    total_members: row.total_members ?? row.totalMembers ?? null,
    totalMembers: row.total_members ?? row.totalMembers ?? null,
    trained_members: row.trained_members ?? row.trainedMembers ?? null,
    trainedMembers: row.trained_members ?? row.trainedMembers ?? null,
    cases_handled: row.cases_handled ?? null,
    casesHandled: row.cases_handled ?? null,
    cases_resolved: row.cases_resolved ?? null,
    casesResolved: row.cases_resolved ?? null,
    resolution_rate_percent: row.resolution_rate_percent ?? null,
    status: row.status ?? '',
    statusLabel: humanize(row.status),
    data_status: dataStatus,
    dataStatus,
    validationStatus: humanize(dataStatus),
    submitted_by: row.submitted_by ?? null,
    submitted_at: row.submitted_at ?? null,
    validated_by: row.validated_by ?? null,
    validated_at: row.validated_at ?? null,
    approved_by: row.approved_by ?? null,
    approved_at: row.approved_at ?? null,
    rejected_at: row.rejected_at ?? null,
    rejection_notes: row.rejection_notes ?? null,
    created_by: row.created_by ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    raw: row,
  };
}

function normalizeRouteRecord(row = {}) {
  const primarySpeciesRaw = row.primary_species ?? row.primarySpecies ?? [];
  const dataStatus = row.data_status ?? row.dataStatus ?? 'draft';
  const agreementStatus = row.agreement_status ?? row.agreementStatus ?? row.formalisation_status ?? row.formalisationStatus ?? '';
  const seasonalUse = row.seasonal_use ?? row.seasonalUse ?? '';
  const originState = row.origin_state ?? row.originState ?? '';
  const destinationState = row.destination_state ?? row.destinationState ?? '';

  return {
    uuid: row.uuid ?? row.id ?? null,
    id: row.uuid ?? row.id ?? null,
    state: row.state ?? '',
    lga: row.lga ?? '',
    origin_state: originState,
    originState,
    destination_state: destinationState,
    destinationState,
    route_name: row.route_name ?? '',
    routeName: row.route_name ?? '',
    origin_text: row.origin_text ?? '',
    origin: row.origin_text ?? originState ?? '',
    destination_text: row.destination_text ?? '',
    destination: row.destination_text ?? destinationState ?? '',
    waypoints: Array.isArray(row.waypoints) ? row.waypoints : [],
    states_traversed: Array.isArray(row.states_traversed) ? row.states_traversed : [],
    statesTraversed: Array.isArray(row.states_traversed) ? row.states_traversed.join(', ') : '',
    lgas_traversed: Array.isArray(row.lgas_traversed) ? row.lgas_traversed : [],
    lgasTraversed: Array.isArray(row.lgas_traversed) ? row.lgas_traversed.join(', ') : '',
    route_length_km: row.route_length_km ?? row.routeLengthKm ?? row.route_width_metres ?? null,
    routeLengthKm: row.route_length_km ?? row.routeLengthKm ?? row.route_width_metres ?? null,
    route_width_metres: row.route_width_metres ?? null,
    routeWidthMetres: row.route_width_metres ?? null,
    encroachment_status: row.encroachment_status ?? '',
    encroachmentStatus: humanize(row.encroachment_status),
    agreement_status: agreementStatus,
    agreementStatus: humanize(agreementStatus),
    formalisation_status: agreementStatus,
    formalisationStatus: humanize(agreementStatus),
    seasonal_use: seasonalUse,
    seasonalUse: humanize(seasonalUse),
    status: humanize(agreementStatus),
    primary_species: Array.isArray(primarySpeciesRaw) ? primarySpeciesRaw : [],
    primarySpeciesUuids: Array.isArray(primarySpeciesRaw) ? primarySpeciesRaw : [],
    primarySpecies: Array.isArray(primarySpeciesRaw) ? primarySpeciesRaw : [],
    annual_throughput: row.annual_throughput ?? null,
    annualThroughput: row.annual_throughput ?? null,
    conflict_history: toBoolean(row.conflict_history),
    conflictHistory: toBoolean(row.conflict_history) ? 'Yes' : 'No',
    primary_conflict_record_uuid: row.primary_conflict_record?.uuid ?? row.primary_conflict_record_uuid ?? null,
    primaryConflictRecordUuid: row.primary_conflict_record?.uuid ?? row.primary_conflict_record_uuid ?? null,
    responsible_authority: row.responsible_authority ?? '',
    responsibleAuthority: row.responsible_authority ?? '',
    data_status: dataStatus,
    dataStatus,
    validationStatus: humanize(dataStatus),
    submitted_by: row.submitted_by ?? null,
    submitted_at: row.submitted_at ?? null,
    validated_by: row.validated_by ?? null,
    validated_at: row.validated_at ?? null,
    approved_by: row.approved_by ?? null,
    approved_at: row.approved_at ?? null,
    rejected_at: row.rejected_at ?? null,
    rejection_notes: row.rejection_notes ?? null,
    created_by: row.created_by ?? null,
    created_at: row.created_at ?? null,
    updated_at: row.updated_at ?? null,
    raw: row,
  };
}

function buildConflictRecordPayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  const wardCommunity = trimOrNull(
    scopedPayload.ward_community ??
    scopedPayload.wardCommunity ??
    [scopedPayload.ward, scopedPayload.community].filter(Boolean).join(' - ')
  );

  return Object.fromEntries(
    Object.entries({
      state: trimOrNull(scopedPayload.state),
      lga: trimOrNull(scopedPayload.lga),
      ward_community: wardCommunity,
      incident_date: trimOrNull(scopedPayload.incident_date ?? scopedPayload.incidentDate),
      conflict_type: trimOrNull(scopedPayload.conflict_type ?? scopedPayload.conflictType),
      severity_level: trimOrNull(scopedPayload.severity_level ?? scopedPayload.severityLevel),
      resolution_status: trimOrNull(scopedPayload.resolution_status ?? scopedPayload.resolutionStatus),
      parties_involved: toArray(scopedPayload.parties_involved ?? scopedPayload.partiesInvolved),
      parties_production_type: toArray(scopedPayload.parties_production_type ?? scopedPayload.partiesProductionType),
      sex_of_primary_parties: trimOrNull(scopedPayload.sex_of_primary_parties ?? scopedPayload.sexOfPrimaryParties),
      age_group_of_parties: trimOrNull(scopedPayload.age_group_of_parties ?? scopedPayload.ageGroupOfParties),
      cause: trimOrNull(scopedPayload.cause ?? scopedPayload.causeOfConflict),
      livestock_lost: toNumberOrNull(scopedPayload.livestock_lost ?? scopedPayload.livestockLost),
      human_casualties: toNumberOrNull(scopedPayload.human_casualties ?? scopedPayload.humanCasualties),
      estimated_economic_loss_ngn: toNumberOrNull(scopedPayload.estimated_economic_loss_ngn ?? scopedPayload.estimatedEconomicLossNgn),
      livelihood_impact: trimOrNull(scopedPayload.livelihood_impact ?? scopedPayload.livelihoodImpact),
      evidence_attachment: trimOrNull(scopedPayload.evidence_attachment ?? scopedPayload.evidenceAttachment),
      evidence_attachment_file_name: trimOrNull(scopedPayload.evidence_attachment_file_name ?? scopedPayload.evidenceAttachmentFileName),
      asset_damage: trimOrNull(scopedPayload.asset_damage ?? scopedPayload.assetDamage),
      reporting_authority: trimOrNull(scopedPayload.reporting_authority ?? scopedPayload.reportingAuthority),
      linked_committee_id: trimOrNull(scopedPayload.linked_committee_id ?? scopedPayload.linkedCommitteeId),
      data_flow_validation_level: trimOrNull(scopedPayload.data_flow_validation_level ?? scopedPayload.dataFlowValidationLevel),
      first_occurrence: toFirstOccurrenceEnum(scopedPayload.first_occurrence ?? scopedPayload.firstOccurrence),
    }).filter(([, value]) => value !== null && value !== undefined)
  );
}

function buildCommitteePayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  return Object.fromEntries(
    Object.entries({
      state: trimOrNull(scopedPayload.state),
      lga: trimOrNull(scopedPayload.lga),
      ward_community: trimOrNull(scopedPayload.ward_community ?? scopedPayload.wardCommunity ?? scopedPayload.ward ?? scopedPayload.community),
      committee_name: trimOrNull(scopedPayload.committee_name ?? scopedPayload.committeeName),
      committee_type: trimOrNull(scopedPayload.committee_type ?? scopedPayload.committeeType),
      chairperson: trimOrNull(scopedPayload.chairperson ?? scopedPayload.chairpersonName ?? scopedPayload.chairpersonActorUuid),
      establishment_authority: trimOrNull(scopedPayload.establishment_authority ?? scopedPayload.establishmentAuth),
      established_date: trimOrNull(scopedPayload.established_date ?? scopedPayload.establishedDate),
      chairperson_actor_uuid: trimOrNull(scopedPayload.chairperson_actor_uuid ?? scopedPayload.chairpersonActorUuid),
      meeting_frequency: trimOrNull(scopedPayload.meeting_frequency ?? scopedPayload.meetingFrequency),
      last_meeting_date: trimOrNull(scopedPayload.last_meeting_date ?? scopedPayload.lastMeetingDate),
      total_members: toNumberOrNull(scopedPayload.total_members ?? scopedPayload.totalMembers),
      trained_members: toNumberOrNull(scopedPayload.trained_members ?? scopedPayload.trainedMembers),
      cases_handled: toNumberOrNull(scopedPayload.cases_handled ?? scopedPayload.casesHandled),
      cases_resolved: toNumberOrNull(scopedPayload.cases_resolved ?? scopedPayload.casesResolved),
      status: trimOrNull(scopedPayload.status),
    }).filter(([, value]) => value !== null && value !== undefined)
  );
}

function buildRoutePayload(payload = {}) {
  const scopedPayload = clampPayloadToCurrentScope(payload);
  return Object.fromEntries(
    Object.entries({
      state: trimOrNull(scopedPayload.state),
      lga: trimOrNull(scopedPayload.lga),
      route_name: trimOrNull(scopedPayload.route_name ?? scopedPayload.routeName),
      origin_state: trimOrNull(scopedPayload.origin_state ?? scopedPayload.originState ?? scopedPayload.origin_text ?? scopedPayload.origin),
      destination_state: trimOrNull(scopedPayload.destination_state ?? scopedPayload.destinationState ?? scopedPayload.destination_text ?? scopedPayload.destination),
      origin_text: trimOrNull(scopedPayload.origin_text ?? scopedPayload.origin),
      destination_text: trimOrNull(scopedPayload.destination_text ?? scopedPayload.destination),
      waypoints: Array.isArray(scopedPayload.waypoints) ? scopedPayload.waypoints : undefined,
      gps_waypoints: Array.isArray(scopedPayload.gps_waypoints) ? scopedPayload.gps_waypoints : (Array.isArray(scopedPayload.waypoints) ? scopedPayload.waypoints : undefined),
      states_traversed: Array.isArray(scopedPayload.states_traversed)
        ? scopedPayload.states_traversed
        : Array.isArray(scopedPayload.statesTraversed)
          ? scopedPayload.statesTraversed
          : trimOrNull(scopedPayload.statesTraversed)?.split(',').map((item) => item.trim()).filter(Boolean),
      lgas_traversed: Array.isArray(scopedPayload.lgas_traversed)
        ? scopedPayload.lgas_traversed
        : Array.isArray(scopedPayload.lgasTraversed)
          ? scopedPayload.lgasTraversed
          : trimOrNull(scopedPayload.lgasTraversed)?.split(',').map((item) => item.trim()).filter(Boolean),
      route_length_km: toNumberOrNull(scopedPayload.route_length_km ?? scopedPayload.routeLengthKm ?? scopedPayload.route_width_metres ?? scopedPayload.routeWidthMetres),
      route_width_metres: toNumberOrNull(scopedPayload.route_width_metres ?? scopedPayload.routeWidthMetres ?? scopedPayload.route_length_km ?? scopedPayload.routeLengthKm),
      agreement_status: trimOrNull(scopedPayload.agreement_status ?? scopedPayload.agreementStatus ?? scopedPayload.formalisation_status ?? scopedPayload.formalisationStatus ?? scopedPayload.status),
      formalisation_status: trimOrNull(scopedPayload.formalisation_status ?? scopedPayload.formalisationStatus ?? scopedPayload.agreement_status ?? scopedPayload.agreementStatus ?? scopedPayload.status),
      seasonal_use: trimOrNull(scopedPayload.seasonal_use ?? scopedPayload.seasonalUse),
      encroachment_status: trimOrNull(scopedPayload.encroachment_status ?? scopedPayload.encroachmentStatus),
      primary_species: Array.isArray(scopedPayload.primary_species)
        ? scopedPayload.primary_species
        : Array.isArray(scopedPayload.primarySpeciesUuids)
          ? scopedPayload.primarySpeciesUuids
          : Array.isArray(scopedPayload.primarySpecies)
            ? scopedPayload.primarySpecies
            : trimOrNull(scopedPayload.primarySpecies)?.split(',').map((item) => item.trim()).filter(Boolean),
      annual_throughput: toNumberOrNull(scopedPayload.annual_throughput ?? scopedPayload.annualThroughput),
      conflict_history: toBoolean(scopedPayload.conflict_history ?? scopedPayload.conflictHistory),
      primary_conflict_record_uuid: trimOrNull(scopedPayload.primary_conflict_record_uuid ?? scopedPayload.primaryConflictRecordUuid),
      responsible_authority: trimOrNull(scopedPayload.responsible_authority ?? scopedPayload.responsibleAuthority),
    }).filter(([, value]) => value !== null && value !== undefined)
  );
}

export const conflictRecordsApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/conflict-records',
  normalize: normalizeConflictRecord,
  buildPayload: buildConflictRecordPayload,
  collectionKey: 'conflict-records',
  defaultPerPage: 25,
}), {
  basePath: '/v1/conflict-records',
  collectionKey: 'conflict-records',
  normalize: normalizeConflictRecord,
  buildPayload: buildConflictRecordPayload,
});

export const conflictCommitteesApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/conflict-committees',
  normalize: normalizeCommitteeRecord,
  buildPayload: buildCommitteePayload,
  collectionKey: 'conflict-committees',
  defaultPerPage: 25,
}), {
  basePath: '/v1/conflict-committees',
  collectionKey: 'conflict-committees',
  normalize: normalizeCommitteeRecord,
  buildPayload: buildCommitteePayload,
});

export const transhumanceRoutesApi = withOfflineCollectionSupport(makeCollectionApi({
  basePath: '/v1/transhumance-routes',
  normalize: normalizeRouteRecord,
  buildPayload: buildRoutePayload,
  collectionKey: 'transhumance-routes',
  defaultPerPage: 25,
}), {
  basePath: '/v1/transhumance-routes',
  collectionKey: 'transhumance-routes',
  normalize: normalizeRouteRecord,
  buildPayload: buildRoutePayload,
});

export const conflictsApi = {
  records: conflictRecordsApi,
  committees: conflictCommitteesApi,
  routes: transhumanceRoutesApi,
};

export {
  buildConflictRecordPayload,
  buildCommitteePayload,
  buildRoutePayload,
  normalizeConflictRecord,
  normalizeCommitteeRecord,
  normalizeRouteRecord,
};
